import { useState, useEffect, useRef } from 'react';
import { supa } from '../lib/supabase.js';
import { TH, CATS } from '../lib/constants.js';
import { compressImage } from '../lib/helpers.js';
import { uploadAvatarToStorage, updateProfileAvatar, fetchRemoteProfile } from '../lib/api.js';
import { Av, Btn, Logo } from '../components/Basic.jsx';
import { t as tr, LOCALE } from '../lib/i18n.js';
// CAPTCHA (Cloudflare Turnstile) sui form di accesso/registrazione/recupero password —
// vedi Auth.jsx TurnstileWidget più sotto per il perché (protegge anche da chi chiama
// l'endpoint di Supabase direttamente, bypassando l'app — cosa che il lockout lato
// database da solo non può fare, perché quello vede solo i tentativi che passano da qui).
// INCOLLA QUI la tua "Site Key" (pubblica, non la Secret Key) una volta creato il sito su
// https://dash.cloudflare.com/?to=/:account/turnstile — finché resta vuota, il widget non
// si mostra e il login/signup/recupero funzionano esattamente come prima (nessuna rottura).
//
// ⚠️ DISATTIVATO IL 29/08/2026: Turnstile blocca il login anche dentro l'app Android
// (dentro l'APK, non solo sul sito), sia per un utente esterno sia per Matte stesso —
// molto probabilmente perché la modalità "managed" di Turnstile non si fida di una
// WebView generica come quella di Capacitor (non ha gli stessi segnali di un vero
// browser Chrome). Da investigare con calma se e come farlo funzionare dentro l'app
// prima di reinserire la site key qui sotto. Il lockout lato database (tentativi falliti
// per account/IP, già implementato altrove) resta comunque attivo come protezione anti
// brute-force anche senza questo controllo.
const TURNSTILE_SITE_KEY = "";
// Calcola l'età in anni interi da una stringa "YYYY-MM-DD", considerando
// se il compleanno di quest'anno è già passato oppure no.
function calcAge(dateStr){
  if(!dateStr)return null;
  const b=new Date(dateStr+"T00:00:00");
  if(isNaN(b.getTime()))return null;
  const now=new Date();
  let age=now.getFullYear()-b.getFullYear();
  const m=now.getMonth()-b.getMonth();
  if(m<0||(m===0&&now.getDate()<b.getDate()))age--;
  return age;
}
// Widget Turnstile: carica lo script di Cloudflare una sola volta (anche se questo
// componente viene montato più volte — login/signup/forgot possono farlo a turno) e
// disegna il riquadro di verifica. onToken riceve il token quando l'utente lo completa
// (spesso automatico, senza tocco — vedi risposta in chat sul perché Turnstile invece di
// hCaptcha) o una stringa vuota se scade/fallisce. resetKey: passare un valore diverso
// (es. un contatore incrementato) ogni volta che serve un token nuovo — i token Turnstile
// si usano una volta sola, quindi va rigenerato dopo ogni tentativo di login/signup, non
// solo dopo un fallimento.
function TurnstileWidget({onToken,resetKey}){
  const boxRef=useRef(null);
  const widgetId=useRef(null);
  useEffect(()=>{
    if(!TURNSTILE_SITE_KEY)return; // non ancora configurato — vedi commento sulla costante più sopra
    let cancelled=false;
    const renderWidget=()=>{
      if(cancelled||!boxRef.current||!window.turnstile)return;
      widgetId.current=window.turnstile.render(boxRef.current,{
        sitekey:TURNSTILE_SITE_KEY,
        callback:(token)=>onToken(token),
        "expired-callback":()=>onToken(""),
        "error-callback":()=>onToken(""),
      });
    };
    if(window.turnstile){ renderWidget(); }
    else {
      const existing=document.querySelector('script[data-turnstile]');
      if(existing){ existing.addEventListener("load",renderWidget); }
      else {
        const s=document.createElement("script");
        s.src="https://challenges.cloudflare.com/turnstile/v0/api.js";
        s.async=true; s.defer=true; s.dataset.turnstile="1";
        s.onload=renderWidget;
        document.head.appendChild(s);
      }
    }
    return ()=>{
      cancelled=true;
      if(window.turnstile&&widgetId.current!=null){ try{ window.turnstile.remove(widgetId.current); }catch(e){} }
    };
  },[]);
  useEffect(()=>{
    if(window.turnstile&&widgetId.current!=null){ try{ window.turnstile.reset(widgetId.current); }catch(e){} }
  },[resetKey]);
  if(!TURNSTILE_SITE_KEY)return null;
  return <div ref={boxRef} style={{margin:"14px 0",display:"flex",justifyContent:"center"}}/>;
}
export function PasswordRecoveryView({onDone}){
  const [pw1,setPw1]=useState("");
  const [pw2,setPw2]=useState("");
  const [busy,setBusy]=useState(false);
  const [err,setErr]=useState("");
  const canSave=pw1.length>=6&&pw1===pw2;
  const save=async()=>{
    if(!canSave||busy)return;
    setErr(""); setBusy(true);
    const {error}=await supa.auth.updateUser({password:pw1});
    setBusy(false);
    if(error){ setErr(error.message); return; }
    onDone();
  };
  return(
    <div className="screen fade" style={{background:"#0A0A12",display:"flex",flexDirection:"column",padding:28}}>
      <div style={{marginTop:70,flex:1}}>
        <div style={{fontSize:22,fontWeight:800,color:"#fff",fontFamily:"Inter,sans-serif",marginBottom:6}}>{tr("auth_pwrecovery_title")}</div>
        <div style={{fontSize:12.5,color:"rgba(255,255,255,.5)",fontFamily:"Inter,sans-serif",marginBottom:28,lineHeight:1.6}}>{tr("auth_pwrecovery_subtitle")}</div>
        <div style={{background:"rgba(255,255,255,.06)",borderRadius:14,padding:"15px",border:"1px solid rgba(255,255,255,.1)",marginBottom:12}}>
          <div style={{fontSize:10,color:"rgba(255,255,255,.38)",fontFamily:"Inter,sans-serif",marginBottom:5,letterSpacing:"1px",textTransform:"uppercase"}}>{tr("set_pw_new_label")}</div>
          <input type="password" value={pw1} onChange={e=>setPw1(e.target.value)} placeholder={tr("set_pw_new_placeholder")} style={{background:"transparent",border:"none",fontSize:16,fontFamily:"Inter,sans-serif",fontWeight:600,color:"#fff",width:"100%"}}/>
        </div>
        <div style={{background:"rgba(255,255,255,.06)",borderRadius:14,padding:"15px",border:"1px solid rgba(255,255,255,.1)",marginBottom:12}}>
          <div style={{fontSize:10,color:"rgba(255,255,255,.38)",fontFamily:"Inter,sans-serif",marginBottom:5,letterSpacing:"1px",textTransform:"uppercase"}}>{tr("auth_pw_repeat_label")}</div>
          <input type="password" value={pw2} onChange={e=>setPw2(e.target.value)} onKeyDown={e=>{if(e.key==="Enter")save();}} placeholder={tr("set_pw_repeat_placeholder")} style={{background:"transparent",border:"none",fontSize:16,fontFamily:"Inter,sans-serif",fontWeight:600,color:"#fff",width:"100%"}}/>
        </div>
        {pw2&&pw1!==pw2&&<div style={{fontSize:11,color:"rgba(255,100,100,.85)",fontFamily:"Inter,sans-serif",marginBottom:8}}>{tr("set_pw_err_mismatch")}</div>}
        {err&&<div style={{fontSize:11,color:"rgba(255,100,100,.85)",fontFamily:"Inter,sans-serif",marginBottom:8}}>{err}</div>}
      </div>
      <Btn full fn={save} style={{background:canSave?"linear-gradient(135deg,#FF2D6B,#7B2FFF)":"rgba(255,255,255,.1)",color:"#fff",padding:"13px",borderRadius:40,fontSize:15,fontWeight:700,fontFamily:"Inter,sans-serif",textAlign:"center",opacity:canSave?(busy?.6:1):.45}}>{busy?tr("set_pw_saving"):tr("auth_pwrecovery_save")}</Btn>
    </div>
  );
}
export function Onboarding({onDone,startRecovering}){
  // Se l'app ci manda qui perché ha trovato una sessione confermata ma
  // nessun profilo (la persona ha appena cliccato il link nell'email),
  // si salta dritti alla scelta di username — niente schermata di
  // benvenuto, niente bisogno di accedere di nuovo a mano.
  const [mode,setMode]=useState(startRecovering?"signup":"welcome"); // welcome | signup | login | forgot | checkEmail
  const [step,setStep]=useState(startRecovering?1:0);
  const [email,setEmail]=useState("");
  const [password,setPassword]=useState("");
  const [birthdate,setBirthdate]=useState("");
  // La data di nascita si costruisce da tre tendine separate (giorno/mese/
  // anno) invece di un <input type="date"> nativo — su alcune WebView
  // Android quel controllo non apre il selettore o non risponde al tocco
  // in modo affidabile, mentre i normali <select> funzionano ovunque.
  const [birthDay,setBirthDay]=useState("");
  const [birthMonth,setBirthMonth]=useState("");
  const [birthYear,setBirthYear]=useState("");
  useEffect(()=>{
    if(birthDay&&birthMonth&&birthYear){
      setBirthdate(`${birthYear}-${String(birthMonth).padStart(2,"0")}-${String(birthDay).padStart(2,"0")}`);
    } else {
      setBirthdate("");
    }
  },[birthDay,birthMonth,birthYear]);
  const birthMonthNames=Array.from({length:12},(_,i)=>{
    const name=new Date(2000,i,1).toLocaleDateString(LOCALE,{month:"long"});
    return name.charAt(0).toUpperCase()+name.slice(1);
  });
  const birthYearNow=new Date().getFullYear();
  const birthYearOptions=Array.from({length:83},(_,i)=>birthYearNow-18-i); // da 18 a 100 anni
  const birthDayOptions=Array.from({length:31},(_,i)=>i+1);
  const birthSelectStyle={flex:1,background:"rgba(255,255,255,.07)",border:"none",borderRadius:8,fontSize:14.5,fontFamily:"Inter,sans-serif",fontWeight:600,color:"#fff",padding:"9px 4px",minWidth:0};
  const [uname,setUname]=useState("");
  const [theme,setTheme]=useState("neon");
  const [cat,setCat]=useState(CATS[0]);
  const [notif,setNotif]=useState("20:00");
  const [busy,setBusy]=useState(false);
  const [err,setErr]=useState("");
  const [recovering,setRecovering]=useState(!!startRecovering); // true una volta creato l'accesso: da qui in poi si tratta solo di completare il profilo
  const [unameAvailable,setUnameAvailable]=useState(null); // null=not checked, true/false=known
  const [checkingUname,setCheckingUname]=useState(false);
  const [newUserId,setNewUserId]=useState(null); // set once the account actually exists, for the avatar step
  const [pickedAvatar,setPickedAvatar]=useState(null); // local preview before upload
  const [uploadingAvatar,setUploadingAvatar]=useState(false);
  const [signupEmail,setSignupEmail]=useState(""); // shown on the "check your email" screen
  // Turnstile: un token per schermata (login/signup/forgot condividono la stessa variabile
  // perché non sono mai visibili insieme), più un contatore che incremento ogni volta che
  // serve rigenerarlo — vedi TurnstileWidget più sopra sul perché.
  const [captchaToken,setCaptchaToken]=useState("");
  const [captchaTick,setCaptchaTick]=useState(0);
  const requireCaptcha=!!TURNSTILE_SITE_KEY;
  const refreshCaptcha=()=>{ setCaptchaToken(""); setCaptchaTick(x=>x+1); };
  const captchaOptions=captchaToken?{captchaToken}:undefined;
  useEffect(()=>{
    if(uname.trim().length<2){ setUnameAvailable(null); return; }
    let active=true;
    setCheckingUname(true);
    const timer=setTimeout(async()=>{
      const existing=await fetchRemoteProfile(uname.trim());
      if(active){ setUnameAvailable(!existing); setCheckingUname(false); }
    },450);
    return ()=>{ active=false; clearTimeout(timer); };
  },[uname]);
  const t=TH[theme]||TH.neon;
  // ── LOGIN ──────────────────────────────────────────────────────────────
  const sendResetEmail=async()=>{
    if(!email.trim().includes("@")||busy)return;
    if(requireCaptcha&&!captchaToken){ setErr(tr("auth_err_captcha_required")); return; }
    setErr(""); setBusy(true);
    try{
      const {error}=await supa.auth.resetPasswordForEmail(email.trim(),{redirectTo:window.location.origin,...captchaOptions});
      refreshCaptcha();
      setBusy(false);
      if(error){ setErr(error.message); return; }
      setErr(tr("auth_forgot_success"));
    }catch(err){
      // Rete di sicurezza aggiunta il 30/08/2026: senza try/catch, un'eccezione imprevista
      // (es. lo storage adapter custom su Android, vedi supabase.js) lasciava setBusy(true)
      // per sempre — schermata bloccata senza nessun errore visibile, "il troubleshoot che
      // non fa fare niente" segnalato da Matte, riprodotto SOLO su Android.
      console.error("Oncer: sendResetEmail — errore imprevisto —",err.message);
      setBusy(false); setErr(tr("auth_err_unexpected"));
    }
  };
  // Minuti d'attesa arrotondati per eccesso (min 1) — niente distinzione minuti/secondi: la
  // policy di lockout è fissa a 15 minuti (vedi add_login_lockout.sql), quindi il caso "sotto
  // il minuto" si presenta solo negli ultimi istanti prima dello sblocco.
  const formatLockoutWait=(seconds)=>Math.max(1,Math.ceil(seconds/60));
  const doLogin=async()=>{
    if(requireCaptcha&&!captchaToken){ setErr(tr("auth_err_captcha_required")); return; }
    setErr(""); setBusy(true);
    try{
    // L'identificativo del lockout è ESATTAMENTE quello che l'utente ha digitato (email o
    // username, non normalizzato oltre trim/lowercase fatti dentro le funzioni RPC): così il
    // blocco copre anche il caso "username sbagliato ripetuto", non solo "password sbagliata
    // per un'email valida" — vedi add_login_lockout.sql per il perché di questa scelta.
    const attemptId=email.trim();
    // Controllo PRIMA di provare qualunque cosa: se già bloccato, niente chiamata a Supabase
    // Auth (né al lookup username), stesso identico messaggio di un tentativo normale fallito.
    const {data:lockCheck,error:lockCheckErr}=await supa.rpc("check_login_lockout",{p_identifier:attemptId});
    if(!lockCheckErr&&lockCheck&&lockCheck.locked){
      setErr(tr("auth_err_locked_out",{minutes:formatLockoutWait(lockCheck.retry_after_seconds)})); setBusy(false); return;
    }
    let loginEmail=email.trim();
    if(!loginEmail.includes("@")){
      // Treat it as a username: resolve to the matching email via the RPC.
      const {data:resolvedEmail,error:lookupErr}=await supa.rpc("get_email_for_username",{uname:loginEmail.toLowerCase()});
      if(lookupErr||!resolvedEmail){
        await supa.rpc("register_login_attempt",{p_identifier:attemptId,p_success:false});
        setErr(tr("auth_err_no_account_username")); setBusy(false); return;
      }
      loginEmail=resolvedEmail;
    }
    const {data,error}=await supa.auth.signInWithPassword({email:loginEmail,password,options:captchaOptions});
    refreshCaptcha();
    if(error){
      const {data:registerResult}=await supa.rpc("register_login_attempt",{p_identifier:attemptId,p_success:false});
      // Se questo tentativo è quello che ha fatto scattare il blocco, lo diciamo subito invece
      // del generico "credenziali sbagliate" — più onesto verso chi ha solo sbagliato a
      // scrivere la password, e scoraggia chi sta tentando a forza bruta.
      if(registerResult&&registerResult.locked){
        setErr(tr("auth_err_locked_out",{minutes:formatLockoutWait(registerResult.retry_after_seconds)})); setBusy(false); return;
      }
      const msg=error.message==="Invalid login credentials"?tr("auth_err_wrong_creds")
        :error.message.toLowerCase().includes("email not confirmed")?tr("auth_err_email_not_confirmed")
        :error.message;
      setErr(msg); setBusy(false); return;
    }
    await supa.rpc("register_login_attempt",{p_identifier:attemptId,p_success:true});
    const {data:prof,error:profErr}=await supa.from("profiles").select("*").eq("id",data.user.id).single();
    setBusy(false);
    if(profErr||!prof){
      // Account exists (auth) but the profile row was never created — recover
      // in place instead of dead-ending, then let them finish setup.
      setRecovering(true); setMode("signup"); setStep(1); setErr("");
      return;
    }
    onDone({username:prof.username,theme:prof.theme||"neon",bio:prof.bio||""});
    }catch(err){
      // Vedi nota identica in sendResetEmail — senza questa rete di sicurezza un'eccezione
      // imprevista (tipica su Android, dove supa usa uno storage adapter custom via Android
      // Keystore — vedi lib/supabase.js) lasciava il bottone bloccato in "busy" senza fine e
      // senza nessun messaggio, indistinguibile da un'app rotta.
      console.error("Oncer: doLogin — errore imprevisto —",err.message);
      setBusy(false); setErr(tr("auth_err_unexpected"));
    }
  };
  // ── SIGNUP — passo 1: crea subito l'accesso (email+password) ───────────
  // Non aspettiamo più che l'utente scelga anche username/tema/orario prima
  // di creare l'account: l'email di conferma parte appena ha le credenziali,
  // così il tempo di attesa (aprire la posta) è "vuoto" invece che dopo aver
  // già compilato tutto il resto.
  const doInitialSignup=async()=>{
    if(requireCaptcha&&!captchaToken){ setErr(tr("auth_err_captcha_required")); return; }
    setErr(""); setBusy(true);
    try{
      const {data,error}=await supa.auth.signUp({email:email.trim(),password,options:captchaOptions});
      refreshCaptcha();
      if(error){ setErr(error.message.includes("already registered")?tr("auth_err_email_registered"):error.message); setBusy(false); return; }
      setBusy(false);
      if(!data.session){
        // Caso normale, con la conferma email attiva: nessuna sessione ancora.
        setSignupEmail(email.trim());
        setMode("checkEmail");
        return;
      }
      // Caso raro (conferma email disattivata): si prosegue subito.
      setRecovering(true);
      setStep(1);
    }catch(err){
      console.error("Oncer: doInitialSignup — errore imprevisto —",err.message);
      setBusy(false); setErr(tr("auth_err_unexpected"));
    }
  };
  // Completa il profilo — usata sia per chi torna dal link nella mail
  // (sessione già pronta, App.jsx ci manda qui direttamente) sia per il
  // caso raro senza conferma email, sia per chi ha fatto accesso e aveva
  // un profilo mancante.
  const finishProfile=async()=>{
    setErr(""); setBusy(true);
    try{
      const {data:sessionData}=await supa.auth.getSession();
      const uid=sessionData&&sessionData.session&&sessionData.session.user.id;
      if(!uid){ setErr(tr("auth_err_session_expired")); setBusy(false); setRecovering(false); setMode("login"); return; }
      const {error:insErr}=await supa.from("profiles").insert({id:uid,username:uname.trim(),theme,bio:"",birthdate:birthdate||null,notif_time:notif});
      setBusy(false);
      if(insErr){ setErr(insErr.message.includes("duplicate")?tr("auth_err_username_taken"):insErr.message); return; }
      setNewUserId(uid); setStep(4);
    }catch(err){
      console.error("Oncer: finishProfile — errore imprevisto —",err.message);
      setBusy(false); setErr(tr("auth_err_unexpected"));
    }
  };
  const handleOnboardAvatarPick=async(e)=>{
    const file=e.target.files&&e.target.files[0];
    e.target.value="";
    if(!file||!newUserId)return;
    setUploadingAvatar(true);
    try{
      const dataUrl=await new Promise((resolve,reject)=>{
        const reader=new FileReader();
        reader.onload=()=>resolve(reader.result);
        reader.onerror=()=>reject(new Error("lettura immagine fallita"));
        reader.readAsDataURL(file);
      });
      setPickedAvatar(dataUrl);
      const compressedBlob=await compressImage(dataUrl,500,0.85);
      const url=await uploadAvatarToStorage(compressedBlob,newUserId);
      await updateProfileAvatar(newUserId,url);
    }catch(err){
      console.error("Oncer: caricamento foto profilo (onboarding) fallito —",err.message);
      setPickedAvatar(null);
    }
    setUploadingAvatar(false);
  };
  const finishOnboarding=()=>{ onDone({username:uname.trim(),theme,bio:"",notif}); };
  if(mode==="welcome") return(
    <div className="screen fade" style={{background:"#0A0A12",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:32,gap:26}}>
      <div style={{position:"absolute",top:40,right:40,width:180,height:180,borderRadius:"50%",background:"rgba(255,45,107,.07)",pointerEvents:"none"}}/>
      <div style={{position:"absolute",bottom:60,left:20,width:140,height:140,borderRadius:"50%",background:"rgba(123,47,255,.07)",pointerEvents:"none"}}/>
      <Logo ac="#FF2D6B" tx="#fff" sz={54} grad/>
      <div style={{textAlign:"center",marginTop:4}}>
        <div style={{fontSize:13,color:"rgba(255,255,255,.5)",fontFamily:"Inter,sans-serif",lineHeight:1.75}}>{tr("auth_tagline_1")}<br/>{tr("auth_tagline_2")}</div>
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:14,width:"100%",maxWidth:280,marginTop:8}}>
        <Btn full fn={()=>{setMode("signup");setStep(0);setErr("");}} style={{background:"linear-gradient(135deg,#FF2D6B,#7B2FFF)",color:"#fff",padding:"15px",borderRadius:40,fontSize:15,fontWeight:700,fontFamily:"Inter,sans-serif",textAlign:"center"}}>{tr("auth_welcome_cta")}</Btn>
        <Btn full fn={()=>{setMode("login");setErr("");}} style={{background:"rgba(255,255,255,.08)",color:"rgba(255,255,255,.65)",padding:"13px",borderRadius:40,fontSize:13,fontWeight:500,fontFamily:"Inter,sans-serif",textAlign:"center",border:"1px solid rgba(255,255,255,.11)"}}>{tr("auth_welcome_have_account")}</Btn>
      </div>
      <div style={{fontSize:10,color:"rgba(255,255,255,.32)",fontFamily:"Inter,sans-serif",textAlign:"center",marginTop:18,lineHeight:1.6,maxWidth:280}}>
        {tr("auth_welcome_terms_notice")}
      </div>
    </div>
  );
  if(mode==="checkEmail") return(
    <div className="screen fade" style={{background:"#0A0A12",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:32,textAlign:"center"}}>
      <div style={{fontSize:56,marginBottom:22}}>📬</div>
      <div style={{fontSize:23,fontWeight:800,color:"#fff",fontFamily:"Inter,sans-serif",marginBottom:12,letterSpacing:"-.4px"}}>{tr("auth_checkemail_title")}</div>
      <div style={{fontSize:13.5,color:"rgba(255,255,255,.55)",fontFamily:"Inter,sans-serif",lineHeight:1.7,marginBottom:6,maxWidth:300}}>
        {tr("auth_checkemail_sent_to")}
      </div>
      <div style={{fontSize:15,fontWeight:700,color:"#fff",fontFamily:"Inter,sans-serif",marginBottom:22,padding:"8px 16px",background:"rgba(255,255,255,.07)",borderRadius:20,wordBreak:"break-all"}}>{signupEmail}</div>
      <div style={{fontSize:12.5,color:"rgba(255,255,255,.45)",fontFamily:"Inter,sans-serif",lineHeight:1.7,marginBottom:34,maxWidth:290}}>
        {tr("auth_checkemail_instructions")}
      </div>
      <div style={{fontSize:11.5,color:"rgba(255,255,255,.32)",fontFamily:"Inter,sans-serif",lineHeight:1.6,marginBottom:26}}>{tr("auth_checkemail_spam_hint")}</div>
      <div onClick={()=>{setMode("login");setErr("");}} style={{fontSize:12.5,color:"#FF7FA6",fontFamily:"Inter,sans-serif",fontWeight:600,cursor:"pointer"}}>{tr("auth_checkemail_confirmed_link")}</div>
    </div>
  );
  if(mode==="login") return(
    <div className="screen fade" style={{background:"#0A0A12",display:"flex",flexDirection:"column",padding:28}}>
      <div style={{marginTop:56,flex:1}}>
        <div style={{fontSize:11,color:"rgba(255,45,107,.8)",fontFamily:"Inter,sans-serif",letterSpacing:"2px",textTransform:"uppercase",marginBottom:12}}>{tr("auth_login_eyebrow")}</div>
        <div style={{fontSize:26,fontWeight:800,color:"#fff",fontFamily:"Inter,sans-serif",letterSpacing:"-.5px",marginBottom:26}}>{tr("auth_login_title")}</div>
        <div style={{background:"rgba(255,255,255,.06)",borderRadius:14,padding:"15px",border:"1px solid rgba(255,255,255,.1)",marginBottom:12}}>
          <div style={{fontSize:10,color:"rgba(255,255,255,.38)",fontFamily:"Inter,sans-serif",marginBottom:5,letterSpacing:"1px",textTransform:"uppercase"}}>{tr("auth_login_id_label")}</div>
          <input type="text" autoCapitalize="none" autoCorrect="off" value={email} onChange={e=>setEmail(e.target.value)} onKeyDown={e=>{if(e.key==="Enter"&&email.trim()&&password&&!busy)doLogin();}} placeholder={tr("auth_login_id_placeholder")} autoFocus style={{background:"transparent",border:"none",fontSize:16,fontFamily:"Inter,sans-serif",fontWeight:600,color:"#fff",width:"100%"}}/>
        </div>
        <div style={{fontSize:10.5,color:"rgba(255,255,255,.3)",fontFamily:"Inter,sans-serif",marginTop:-6,marginBottom:12,lineHeight:1.5}}>{tr("auth_login_username_hint")}</div>
        <div style={{background:"rgba(255,255,255,.06)",borderRadius:14,padding:"15px",border:"1px solid rgba(255,255,255,.1)"}}>
          <div style={{fontSize:10,color:"rgba(255,255,255,.38)",fontFamily:"Inter,sans-serif",marginBottom:5,letterSpacing:"1px",textTransform:"uppercase"}}>{tr("auth_password_label")}</div>
          <input type="password" value={password} onChange={e=>setPassword(e.target.value)} onKeyDown={e=>{if(e.key==="Enter"&&email.trim()&&password&&!busy)doLogin();}} placeholder="••••••••" style={{background:"transparent",border:"none",fontSize:16,fontFamily:"Inter,sans-serif",fontWeight:600,color:"#fff",width:"100%"}}/>
        </div>
        <TurnstileWidget onToken={setCaptchaToken} resetKey={captchaTick}/>
        {err&&<div style={{fontSize:11.5,color:"#FF8FAE",fontFamily:"Inter,sans-serif",marginTop:12,lineHeight:1.5}}>{err}</div>}
        <div onClick={()=>{setMode("forgot");setErr("");}} style={{fontSize:12,color:"rgba(255,255,255,.45)",fontFamily:"Inter,sans-serif",marginTop:14,textAlign:"center",cursor:"pointer"}}>{tr("auth_login_forgot")}</div>
      </div>
      <div style={{display:"flex",gap:11,marginTop:20}}>
        <Btn fn={()=>setMode("welcome")} style={{width:44,height:44,borderRadius:"50%",background:"rgba(255,255,255,.07)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,color:"rgba(255,255,255,.44)"}}>←</Btn>
        <Btn full fn={()=>{if(email.trim()&&password&&!busy)doLogin();}} style={{background:(email.trim()&&password)?"linear-gradient(135deg,#FF2D6B,#7B2FFF)":"rgba(255,255,255,.1)",color:"#fff",padding:"13px",borderRadius:40,fontSize:15,fontWeight:700,fontFamily:"Inter,sans-serif",textAlign:"center",flex:1,opacity:busy?.6:1}}>{busy?tr("auth_login_submitting"):tr("auth_login_submit")}</Btn>
      </div>
    </div>
  );
  if(mode==="forgot") return(
    <div className="screen fade" style={{background:"#0A0A12",display:"flex",flexDirection:"column",padding:28}}>
      <div style={{marginTop:56,flex:1}}>
        <div style={{fontSize:11,color:"rgba(255,45,107,.8)",fontFamily:"Inter,sans-serif",letterSpacing:"2px",textTransform:"uppercase",marginBottom:12}}>{tr("auth_forgot_eyebrow")}</div>
        <div style={{fontSize:24,fontWeight:800,color:"#fff",fontFamily:"Inter,sans-serif",letterSpacing:"-.5px",marginBottom:10}}>{tr("auth_forgot_title")}</div>
        <div style={{fontSize:13,color:"rgba(255,255,255,.42)",fontFamily:"Inter,sans-serif",marginBottom:22,lineHeight:1.6}}>{tr("auth_forgot_subtitle")}</div>
        <div style={{background:"rgba(255,255,255,.06)",borderRadius:14,padding:"15px",border:"1px solid rgba(255,255,255,.1)"}}>
          <div style={{fontSize:10,color:"rgba(255,255,255,.38)",fontFamily:"Inter,sans-serif",marginBottom:5,letterSpacing:"1px",textTransform:"uppercase"}}>{tr("set_row_email")}</div>
          <input type="email" autoCapitalize="none" autoCorrect="off" value={email} onChange={e=>setEmail(e.target.value)} onKeyDown={e=>{if(e.key==="Enter"&&email.trim().includes("@")&&!busy)sendResetEmail();}} placeholder={tr("auth_forgot_email_placeholder")} autoFocus style={{background:"transparent",border:"none",fontSize:16,fontFamily:"Inter,sans-serif",fontWeight:600,color:"#fff",width:"100%"}}/>
        </div>
        <TurnstileWidget onToken={setCaptchaToken} resetKey={captchaTick}/>
        {err&&<div style={{fontSize:11.5,color:err.startsWith("✓")?"#8FE0B0":"#FF8FAE",fontFamily:"Inter,sans-serif",marginTop:12,lineHeight:1.5}}>{err}</div>}
        <div onClick={()=>{window.location.href="mailto:hello@oncer.app?subject=Non%20riesco%20ad%20accedere";}} style={{fontSize:11.5,color:"rgba(255,255,255,.32)",fontFamily:"Inter,sans-serif",marginTop:20,textAlign:"center",lineHeight:1.6,cursor:"pointer"}}>{tr("auth_forgot_cant_access")} <span style={{textDecoration:"underline"}}>{tr("auth_forgot_write_us")}</span></div>
      </div>
      <div style={{display:"flex",gap:11,marginTop:20}}>
        <Btn fn={()=>{setMode("login");setErr("");}} style={{width:44,height:44,borderRadius:"50%",background:"rgba(255,255,255,.07)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,color:"rgba(255,255,255,.44)"}}>←</Btn>
        <Btn full fn={sendResetEmail} style={{background:email.trim().includes("@")?"linear-gradient(135deg,#FF2D6B,#7B2FFF)":"rgba(255,255,255,.1)",color:"#fff",padding:"13px",borderRadius:40,fontSize:15,fontWeight:700,fontFamily:"Inter,sans-serif",textAlign:"center",flex:1,opacity:busy?.6:1}}>{busy?tr("auth_forgot_submitting"):tr("auth_forgot_submit")}</Btn>
      </div>
    </div>
  );
  if(mode==="signup"&&step===0) return(
    <div className="screen fade" style={{background:"#0A0A12",display:"flex",flexDirection:"column",padding:28}}>
      <div style={{marginTop:56,flex:1}}>
        <div style={{fontSize:11,color:"rgba(255,45,107,.8)",fontFamily:"Inter,sans-serif",letterSpacing:"2px",textTransform:"uppercase",marginBottom:12}}>{tr("auth_signup0_eyebrow")}</div>
        <div style={{fontSize:26,fontWeight:800,color:"#fff",fontFamily:"Inter,sans-serif",letterSpacing:"-.5px",marginBottom:8}}>{tr("auth_signup0_title")}</div>
        <div style={{fontSize:13,color:"rgba(255,255,255,.42)",fontFamily:"Inter,sans-serif",marginBottom:22,lineHeight:1.6}}>{tr("auth_signup0_subtitle")}</div>
        <div style={{background:"rgba(255,255,255,.06)",borderRadius:14,padding:"15px",border:"1px solid rgba(255,255,255,.1)",marginBottom:12}}>
          <div style={{fontSize:10,color:"rgba(255,255,255,.38)",fontFamily:"Inter,sans-serif",marginBottom:5,letterSpacing:"1px",textTransform:"uppercase"}}>{tr("set_row_email")}</div>
          <input type="email" value={email} onChange={e=>setEmail(e.target.value)} onKeyDown={e=>{if(e.key==="Enter"&&!busy){setErr("");if(email.trim().includes("@")&&password.length>=6){if(birthdate&&calcAge(birthdate)>=18)doInitialSignup();else setErr(tr("auth_err_underage"));}else setErr(tr("auth_err_validation"));}}} placeholder={tr("auth_forgot_email_placeholder")} autoFocus style={{background:"transparent",border:"none",fontSize:16,fontFamily:"Inter,sans-serif",fontWeight:600,color:"#fff",width:"100%"}}/>
        </div>
        <div style={{background:"rgba(255,255,255,.06)",borderRadius:14,padding:"15px",border:"1px solid rgba(255,255,255,.1)",marginBottom:12}}>
          <div style={{fontSize:10,color:"rgba(255,255,255,.38)",fontFamily:"Inter,sans-serif",marginBottom:5,letterSpacing:"1px",textTransform:"uppercase"}}>{tr("auth_password_label")}</div>
          <input type="password" value={password} onChange={e=>setPassword(e.target.value)} onKeyDown={e=>{if(e.key==="Enter"&&!busy){setErr("");if(email.trim().includes("@")&&password.length>=6){if(birthdate&&calcAge(birthdate)>=18)doInitialSignup();else setErr(tr("auth_err_underage"));}else setErr(tr("auth_err_validation"));}}} placeholder={tr("auth_signup0_password_placeholder")} style={{background:"transparent",border:"none",fontSize:16,fontFamily:"Inter,sans-serif",fontWeight:600,color:"#fff",width:"100%"}}/>
        </div>
        <div style={{background:"rgba(255,255,255,.06)",borderRadius:14,padding:"15px",border:"1px solid rgba(255,255,255,.1)"}}>
          <div style={{fontSize:10,color:"rgba(255,255,255,.38)",fontFamily:"Inter,sans-serif",marginBottom:5,letterSpacing:"1px",textTransform:"uppercase"}}>{tr("auth_signup0_birthdate_label")}</div>
          <div style={{display:"flex",gap:7}}>
            <select value={birthDay} onChange={e=>setBirthDay(e.target.value)} style={birthSelectStyle}>
              <option value="">{tr("auth_birthdate_day")}</option>
              {birthDayOptions.map(d=><option key={d} value={d}>{d}</option>)}
            </select>
            <select value={birthMonth} onChange={e=>setBirthMonth(e.target.value)} style={{...birthSelectStyle,flex:1.7}}>
              <option value="">{tr("auth_birthdate_month")}</option>
              {birthMonthNames.map((name,i)=><option key={i} value={i+1}>{name}</option>)}
            </select>
            <select value={birthYear} onChange={e=>setBirthYear(e.target.value)} style={birthSelectStyle}>
              <option value="">{tr("auth_birthdate_year")}</option>
              {birthYearOptions.map(y=><option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </div>
        <TurnstileWidget onToken={setCaptchaToken} resetKey={captchaTick}/>
        {err&&<div style={{fontSize:11.5,color:"#FF8FAE",fontFamily:"Inter,sans-serif",marginTop:12,lineHeight:1.5}}>{err}</div>}
      </div>
      <div style={{display:"flex",gap:11,marginTop:20}}>
        <Btn fn={()=>setMode("welcome")} style={{width:44,height:44,borderRadius:"50%",background:"rgba(255,255,255,.07)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,color:"rgba(255,255,255,.44)"}}>←</Btn>
        <Btn full fn={()=>{if(busy)return;setErr("");if(email.trim().includes("@")&&password.length>=6){if(birthdate&&calcAge(birthdate)>=18)doInitialSignup();else setErr(tr("auth_err_underage"));}else setErr(tr("auth_err_validation"));}} style={{background:"linear-gradient(135deg,#FF2D6B,#7B2FFF)",color:"#fff",padding:"13px",borderRadius:40,fontSize:15,fontWeight:700,fontFamily:"Inter,sans-serif",textAlign:"center",flex:1,opacity:busy?.6:1}}>{busy?tr("auth_creating"):tr("auth_continue")}</Btn>
      </div>
    </div>
  );
  if(mode==="signup"&&step===1) return(
    <div className="screen fade" style={{background:"#0A0A12",display:"flex",flexDirection:"column",padding:28}}>
      <div style={{marginTop:56,flex:1}}>
        <div style={{fontSize:11,color:"rgba(255,45,107,.8)",fontFamily:"Inter,sans-serif",letterSpacing:"2px",textTransform:"uppercase",marginBottom:12}}>{tr("auth_signup1_eyebrow")}</div>
        <div style={{fontSize:26,fontWeight:800,color:"#fff",fontFamily:"Inter,sans-serif",letterSpacing:"-.5px",marginBottom:8}}>{tr("auth_signup1_title")}</div>
        <div style={{fontSize:13,color:"rgba(255,255,255,.42)",fontFamily:"Inter,sans-serif",marginBottom:26,lineHeight:1.6}}>{tr("auth_signup1_subtitle")}</div>
        <div style={{background:"rgba(255,255,255,.06)",borderRadius:14,padding:"15px",border:"1px solid rgba(255,255,255,.1)"}}>
          <div style={{fontSize:10,color:"rgba(255,255,255,.38)",fontFamily:"Inter,sans-serif",marginBottom:5,letterSpacing:"1px",textTransform:"uppercase"}}>{tr("set_row_username")}</div>
          <input value={uname} onChange={e=>setUname(e.target.value.replace(/[^a-zA-Z0-9_.]/g,"").toLowerCase())} onKeyDown={e=>{if(e.key==="Enter"&&uname.trim().length>=2&&unameAvailable!==false)setStep(2);}} placeholder={tr("auth_signup1_username_placeholder")} maxLength={30} autoFocus style={{background:"transparent",border:"none",fontSize:19,fontFamily:"Inter,sans-serif",fontWeight:700,color:"#fff",width:"100%",letterSpacing:"-.3px"}}/>
        </div>
        {uname.length>0&&uname.length<2&&<div style={{fontSize:11,color:"rgba(255,100,100,.7)",fontFamily:"Inter,sans-serif",marginTop:8}}>{tr("auth_signup1_min_chars")}</div>}
        {uname.trim().length>=2&&checkingUname&&<div style={{fontSize:11,color:"rgba(255,255,255,.4)",fontFamily:"Inter,sans-serif",marginTop:8}}>{tr("auth_signup1_checking")}</div>}
        {uname.trim().length>=2&&!checkingUname&&unameAvailable===true&&<div style={{fontSize:11,color:"#6EE7A0",fontFamily:"Inter,sans-serif",marginTop:8}}>{tr("auth_signup1_available")}</div>}
        {uname.trim().length>=2&&!checkingUname&&unameAvailable===false&&<div style={{fontSize:11,color:"rgba(255,100,100,.85)",fontFamily:"Inter,sans-serif",marginTop:8}}>{tr("auth_signup1_taken")}</div>}
        {err&&<div style={{fontSize:11.5,color:"#FF8FAE",fontFamily:"Inter,sans-serif",marginTop:12,lineHeight:1.5}}>{err}</div>}
      </div>
      <div style={{display:"flex",gap:11,marginTop:20}}>
        <Btn full fn={()=>{if(uname.trim().length>=2&&unameAvailable!==false&&!checkingUname)setStep(2);}} style={{background:(uname.trim().length>=2&&unameAvailable!==false&&!checkingUname)?"linear-gradient(135deg,#FF2D6B,#7B2FFF)":"rgba(255,255,255,.1)",color:"#fff",padding:"13px",borderRadius:40,fontSize:15,fontWeight:700,fontFamily:"Inter,sans-serif",textAlign:"center",flex:1,opacity:(uname.trim().length>=2&&unameAvailable!==false&&!checkingUname)?1:.45}}>{tr("auth_continue")}</Btn>
      </div>
    </div>
  );
  if(mode==="signup"&&step===2) return(
    <div className="screen fade" style={{background:t.bg,display:"flex",flexDirection:"column"}}>
      <div style={{padding:"26px 18px 12px",flexShrink:0}}>
        <div style={{fontSize:11,color:t.ac,fontFamily:"Inter,sans-serif",letterSpacing:"2px",textTransform:"uppercase",marginBottom:11}}>{tr("auth_signup2_eyebrow")}</div>
        <div style={{fontSize:22,fontWeight:800,color:t.tx,fontFamily:"Inter,sans-serif",letterSpacing:"-.5px",marginBottom:4}}>{tr("auth_signup2_title")}</div>
        <div style={{fontSize:12,color:t.soft,fontFamily:"Inter,sans-serif",marginBottom:12}}>{tr("auth_signup2_subtitle")}</div>
        <div style={{display:"flex",gap:6,overflowX:"auto",scrollbarWidth:"none",paddingBottom:2}}>
          {CATS.map(c=><div key={c} onClick={()=>setCat(c)} style={{flexShrink:0,padding:"5px 10px",borderRadius:20,cursor:"pointer",background:cat===c?t.pill:t.soft+"18",fontSize:10,fontFamily:"Inter,sans-serif",fontWeight:700,color:cat===c?"#fff":t.soft}}>{c}</div>)}
        </div>
      </div>
      <div style={{flex:1,overflowY:"auto",padding:"0 16px"}}>
        <div style={{display:"flex",flexWrap:"wrap",gap:10,paddingBottom:14}}>
          {Object.values(TH).filter(x=>x.cat===cat).map(x=>{
            const act=theme===x.id;
            return(
              <div key={x.id} onClick={()=>setTheme(x.id)} style={{width:"calc(50% - 5px)",background:x.bg,borderRadius:13,padding:"13px",border:act?`2px solid ${x.ac}`:`1px solid ${x.ac}22`,cursor:"pointer",boxShadow:act?`0 0 0 1px ${x.ac}44,0 4px 14px ${x.ac}22`:"none"}}>
                <div style={{width:38,height:38,borderRadius:10,background:x.pill,display:"flex",alignItems:"center",justifyContent:"center",fontSize:19,marginBottom:7}}>{x.emoji}</div>
                <div style={{fontSize:13,fontWeight:700,color:x.tx,fontFamily:"Inter,sans-serif"}}>{x.name}</div>
                <div style={{fontSize:10,color:x.soft,fontFamily:"Inter,sans-serif",marginTop:1}}>{x.cat}</div>
                {act&&<div style={{marginTop:6,fontSize:10,color:x.ac,fontFamily:"Inter,sans-serif",fontWeight:700}}>{tr("auth_signup2_selected")}</div>}
              </div>
            );
          })}
        </div>
      </div>
      <div style={{padding:"11px 16px 24px",display:"flex",gap:11,flexShrink:0,borderTop:`1px solid ${t.bord}`}}>
        <Btn fn={()=>setStep(1)} style={{width:44,height:44,borderRadius:"50%",background:t.card,border:`1px solid ${t.bord}`,color:t.tx,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18}}>←</Btn>
        <Btn full fn={()=>setStep(3)} style={{background:t.pill,color:"#fff",padding:"13px",borderRadius:40,fontSize:15,fontWeight:700,fontFamily:"Inter,sans-serif",textAlign:"center",flex:1}}>{tr("auth_continue")}</Btn>
      </div>
    </div>
  );
  if(mode==="signup"&&step===3) return(
    <div className="screen fade" style={{background:t.bg,display:"flex",flexDirection:"column",padding:28}}>
      <div style={{marginTop:56,flex:1}}>
        <div style={{fontSize:11,color:t.ac,fontFamily:"Inter,sans-serif",letterSpacing:"2px",textTransform:"uppercase",marginBottom:11}}>{tr("auth_signup3_eyebrow")}</div>
        <div style={{fontSize:22,fontWeight:800,color:t.tx,fontFamily:"Inter,sans-serif",letterSpacing:"-.5px",marginBottom:4}}>{tr("auth_signup3_title")}</div>
        <div style={{fontSize:12,color:t.soft,fontFamily:"Inter,sans-serif",marginBottom:24,lineHeight:1.6}}>{tr("auth_signup3_subtitle")}</div>
        <div style={{display:"flex",flexDirection:"column",gap:9}}>
          {["07:00","09:00","12:00","18:00","20:00","22:00"].map(time=>(
            <div key={time} onClick={()=>setNotif(time)} style={{background:t.card,borderRadius:12,padding:"13px 15px",border:notif===time?`1.5px solid ${t.ac}`:`1px solid ${t.bord}`,display:"flex",alignItems:"center",justifyContent:"space-between",cursor:"pointer"}}>
              <span style={{fontSize:14,fontWeight:notif===time?700:400,color:t.tx,fontFamily:"Inter,sans-serif"}}>{time}</span>
              {notif===time&&<span style={{color:t.ac,fontSize:18}}>✓</span>}
            </div>
          ))}
        </div>
        {err&&<div style={{fontSize:11.5,color:"#D93025",fontFamily:"Inter,sans-serif",marginTop:14,lineHeight:1.5}}>{err}</div>}
      </div>
      <div style={{display:"flex",gap:11,marginTop:22}}>
        <Btn fn={()=>setStep(2)} style={{width:44,height:44,borderRadius:"50%",background:t.card,border:`1px solid ${t.bord}`,color:t.tx,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18}}>←</Btn>
        <Btn full fn={()=>{if(!busy)finishProfile();}} style={{background:t.pill,color:"#fff",padding:"13px",borderRadius:40,fontSize:15,fontWeight:700,fontFamily:"Inter,sans-serif",textAlign:"center",flex:1,opacity:busy?.6:1}}>{busy?tr("auth_creating"):tr("auth_signup3_submit")}</Btn>
      </div>
    </div>
  );
  return(
    <div className="screen fade" style={{background:t.bg,display:"flex",flexDirection:"column",padding:28}}>
      <div style={{marginTop:56,flex:1,display:"flex",flexDirection:"column",alignItems:"center"}}>
        <div style={{fontSize:11,color:t.ac,fontFamily:"Inter,sans-serif",letterSpacing:"2px",textTransform:"uppercase",marginBottom:11,alignSelf:"flex-start"}}>{tr("auth_signup4_eyebrow")}</div>
        <div style={{fontSize:22,fontWeight:800,color:t.tx,fontFamily:"Inter,sans-serif",letterSpacing:"-.5px",marginBottom:4,alignSelf:"flex-start"}}>{tr("auth_signup4_title")}</div>
        <div style={{fontSize:12,color:t.soft,fontFamily:"Inter,sans-serif",marginBottom:26,lineHeight:1.6,alignSelf:"flex-start"}}>{tr("auth_signup4_subtitle")}</div>
        <label style={{position:"relative",cursor:"pointer",marginBottom:10}}>
          <input type="file" accept="image/*" style={{display:"none"}} onChange={handleOnboardAvatarPick} disabled={uploadingAvatar}/>
          <Av u={uname||"tu"} tid={theme} sz={104} photo={pickedAvatar}/>
          <div style={{position:"absolute",bottom:0,right:0,width:32,height:32,borderRadius:"50%",background:t.pill,display:"flex",alignItems:"center",justifyContent:"center",fontSize:15,boxShadow:`0 0 0 3px ${t.bg}`}}>{uploadingAvatar?"⏳":pickedAvatar?"✓":"+"}</div>
        </label>
        <div style={{fontSize:11,color:t.soft,fontFamily:"Inter,sans-serif"}}>{uploadingAvatar?tr("set_avatar_uploading"):pickedAvatar?tr("auth_signup4_saved"):tr("auth_signup4_tap_hint")}</div>
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:10,marginTop:22}}>
        <Btn full fn={finishOnboarding} style={{background:t.pill,color:"#fff",padding:"13px",borderRadius:40,fontSize:15,fontWeight:700,fontFamily:"Inter,sans-serif",textAlign:"center",opacity:uploadingAvatar?.6:1}}>{pickedAvatar?tr("auth_signup4_finish_with_photo"):tr("auth_signup4_finish_no_photo")}</Btn>
        {!pickedAvatar&&<Btn fn={finishOnboarding} style={{background:"transparent",color:t.soft,padding:"9px",borderRadius:40,fontSize:12.5,fontFamily:"Inter,sans-serif",textAlign:"center"}}>{tr("auth_signup4_skip")}</Btn>}
      </div>
    </div>
  );
}
